/**
 * Snapshot detail view (US-033.5).
 *
 * Read-only snapshot of what NIL Go saw + a "Record decision" action so
 * a compliance officer can log NIL Go's verdict on behalf of the athlete
 * (the `export_only` provider has no callback — someone has to type
 * what NIL Go's UI showed). Mutates via `compliance.recordDecision`,
 * which authorizes via shared-org membership and bypasses ZenStack
 * cross-user policies the same way the eligibility verify path does.
 *
 * Org-scoped via the router's `submissionDetail` handler — if the
 * caller doesn't share an org with the submission's athlete, the
 * server 403s and the error component renders below.
 */
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@app/ui';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../../../lib/api';

type RecordableStatus = 'approved' | 'needs_revision' | 'rejected' | 'under_review';

export const Route = createFileRoute('/_authenticated/compliance/submissions/$submissionId')({
  loader: async ({ context, params }) => {
    const { getServerApi } = await import('../../../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(
      serverApi.compliance.submissionDetail.queryOptions({ input: { id: params.submissionId } }),
    );
  },
  errorComponent: ({ error }) => (
    <div className="max-w-2xl">
      <Link to="/compliance/submissions" className="text-sm text-muted-foreground hover:underline">
        ← Back to submissions
      </Link>
      <Card className="mt-4">
        <CardContent className="py-12 text-center">
          <p className="text-red-600">
            {error instanceof Error ? error.message : 'Failed to load submission'}
          </p>
        </CardContent>
      </Card>
    </div>
  ),
  component: SubmissionDetailPage,
});

function SubmissionDetailPage() {
  const { submissionId } = Route.useParams();
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(
    api.compliance.submissionDetail.queryOptions({ input: { id: submissionId } }),
  );

  const terminal = data.status === 'approved' || data.status === 'cancelled';
  const [status, setStatus] = useState<RecordableStatus | ''>('');
  const [reason, setReason] = useState('');

  const recordMutation = useMutation({
    ...api.compliance.recordDecision.mutationOptions(),
    onSuccess: () => {
      toast.success('Decision recorded — the athlete has been notified.');
      setStatus('');
      setReason('');
      // Bust both the detail view + the dashboard rollup so the new
      // status shows everywhere without a refresh.
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey?.[0]) && q.queryKey[0][0] === 'compliance',
      });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Record failed'),
  });

  const handleRecord = () => {
    if (!status) {
      toast.error('Pick a decision');
      return;
    }
    if ((status === 'needs_revision' || status === 'rejected') && !reason.trim()) {
      toast.error('Add the reason NIL Go gave');
      return;
    }
    recordMutation.mutate({ submissionId, status, reason: reason.trim() || undefined });
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <Link
          to="/compliance/submissions"
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to submissions
        </Link>
        <h1 className="text-2xl font-bold mt-2">
          {data.deal.title}{' '}
          <span className="text-base font-normal text-muted-foreground">
            · revision #{data.revisionNumber}
          </span>
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data.athlete.name} · {data.athlete.email}
          {data.athlete.school ? ` · ${data.athlete.school}` : ''}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Submission state</CardTitle>
          <CardDescription>
            What we recorded for this revision — the snapshot below is what NIL Go saw.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid sm:grid-cols-2 gap-3 text-sm">
            <DL label="Status" value={data.status.replace(/_/g, ' ')} />
            <DL label="Provider" value={data.provider} />
            <DL label="Submitted at" value={data.submittedAt ?? '—'} />
            <DL label="Decided at" value={data.decidedAt ?? '—'} />
            <DL
              label="External submission id"
              value={data.externalSubmissionId ?? '—'}
              mono
            />
            <DL label="Created at" value={data.createdAt} />
          </dl>
          {data.decisionReason && (
            <div className="mt-4 rounded bg-muted/40 p-3 text-sm">
              <span className="font-medium">Decision note:</span> {data.decisionReason}
            </div>
          )}
          {data.auditLog && (
            <div className="mt-2 rounded bg-muted/40 p-3 text-xs text-muted-foreground whitespace-pre-wrap">
              {data.auditLog}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Record NIL Go's decision</CardTitle>
          <CardDescription>
            {terminal
              ? data.status === 'approved'
                ? 'Approved is terminal. To change the outcome the athlete needs to resubmit (creates a new revision).'
                : 'This submission was cancelled — record the decision on a fresh resubmission instead.'
              : 'NIL Go gave you a verdict offline. Type it in and the athlete gets notified — same status the row will show on every dashboard from here on.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!terminal && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Decision</Label>
                  <Select
                    value={status}
                    onValueChange={(v) => setStatus(v as RecordableStatus)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pick a decision…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="needs_revision">Needs revision</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="under_review">Under review</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">
                  Reason / note{' '}
                  {(status === 'needs_revision' || status === 'rejected') && (
                    <span className="text-red-600">(required)</span>
                  )}
                </Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Paste NIL Go's reason verbatim so the athlete can fix it (or leave blank for approved)."
                  rows={4}
                  maxLength={2000}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={handleRecord}
                  disabled={!status || recordMutation.isPending}
                >
                  {recordMutation.isPending ? 'Recording…' : 'Record decision'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payload snapshot</CardTitle>
          <CardDescription>
            Immutable — what NIL Go saw at submit-time. Later edits to the deal don't change this.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="text-xs bg-muted/30 rounded p-3 overflow-x-auto">
            {JSON.stringify(data.payloadSnapshot, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}

function DL({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono text-xs' : 'text-sm'}>{value}</dd>
    </div>
  );
}
